// client/src/components/pages/PeriodSummary/RoutePicker.js
//
// The toolbar's route filter (ST-00174 UX pass, item 17; groupings since the
// 2026-09-15 follow-up).
//
// The options are the tenant's Route Groupings — the routes set up under
// Settings → Route Summary — not the raw dispatch→receipt pairs in the data, so
// this page filters by the same routes Route Summary reports on.
//
// Why not `common/ThemedSelect`: that is react-select with a fixed option shape
// (label + value). This list needs a muted per-option stat line and a search
// box, so it is a small purpose-built combobox instead, following the same
// combobox/listbox pattern as Settings/GroupingModal.
//
// Option identity is a single string so the whole thing stays a plain listbox:
//   'all' | `g:<groupingId>` | `pair:<dispatch><SEP><receipt>`
// A `pair:` value is never offered here — it is what "Filter to this route" on
// a Route breakdown row sets — but the picker button and the chip still have to
// label it.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaSearch, FaChevronDown, FaTimes } from 'react-icons/fa';
import { formatCount } from './utils';

// Unit separator: cannot occur in a location name.
const SEP = String.fromCharCode(31);
const BLANK = '(blank)';

export const ALL_ROUTES = 'all';

const EMPTY_FILTER = { groupingId: null, dispatchLocation: null, receiptLocation: null };

/** The picker value for a route filter, or `all`. A grouping wins over legs. */
export const routeOptionId = ({ groupingId, dispatchLocation, receiptLocation } = {}) => {
  if (groupingId !== null && groupingId !== undefined) return `g:${groupingId}`;
  const hasD = dispatchLocation !== null && dispatchLocation !== undefined;
  const hasR = receiptLocation !== null && receiptLocation !== undefined;
  if (hasD || hasR) return `pair:${hasD ? dispatchLocation : ''}${SEP}${hasR ? receiptLocation : ''}`;
  return ALL_ROUTES;
};

/** Inverse of routeOptionId — what the API params for an option id are. */
export const routeFilterOf = (id) => {
  if (!id || id === ALL_ROUTES) return { ...EMPTY_FILTER };
  if (id.startsWith('g:')) {
    const groupingId = Number(id.slice(2));
    return Number.isInteger(groupingId) && groupingId > 0
      ? { ...EMPTY_FILTER, groupingId }
      : { ...EMPTY_FILTER };
  }
  if (id.startsWith('pair:')) {
    const [dispatchLocation, receiptLocation] = id.slice(5).split(SEP);
    return { groupingId: null, dispatchLocation, receiptLocation };
  }
  return { ...EMPTY_FILTER };
};

const show = (location) => (location === '' ? BLANK : location);

/**
 * `Pit F → Seriti` (the grouping's name), `Pit F → Seriti R545` for a raw pair.
 * @param {string} id       option id
 * @param {Array}  groupings rows from GET /api/period-summary/groupings, for names
 */
export const routeOptionLabel = (id, groupings = []) => {
  if (!id || id === ALL_ROUTES) return 'All routes';
  const { groupingId, dispatchLocation, receiptLocation } = routeFilterOf(id);
  if (groupingId !== null) {
    const g = groupings.find((row) => Number(row.id) === groupingId);
    return g ? g.name : `Route #${groupingId}`;
  }
  return `${show(dispatchLocation)} → ${show(receiptLocation)}`;
};

/** The option list: "All routes" then one option per grouping (already A–Z). */
const buildOptions = (groupings) => [
  { id: ALL_ROUTES, label: 'All routes' },
  ...groupings.map((g) => ({
    id: `g:${g.id}`,
    label: g.name,
    description: g.description || null,
    stat: `${formatCount(g.tickets)} tkt · ${formatCount(g.keys)} keys`,
  })),
];

/**
 * @param {object}   props
 * @param {Array}    props.groupings  rows from GET /api/period-summary/groupings
 * @param {boolean}  props.loading
 * @param {string}   props.value      option id ('all' when unfiltered)
 * @param {Function} props.onChange   (optionId) => void
 */
const RoutePicker = ({ groupings = [], loading = false, value = ALL_ROUTES, onChange }) => {
  const [open, setOpen] = useState(false);
  const [queryText, setQueryText] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const options = useMemo(() => buildOptions(groupings), [groupings]);

  const filtered = useMemo(() => {
    const term = queryText.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (item) =>
        item.label.toLowerCase().includes(term) ||
        (item.description && item.description.toLowerCase().includes(term))
    );
  }, [options, queryText]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQueryText('');
      setActiveIndex(0);
      // Focus after the popover paints.
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const choose = (id) => {
    onChange(id);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!filtered.length) return;
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((prev) => (prev + delta + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIndex]) choose(filtered[activeIndex].id);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
    }
  };

  const activeId = filtered[activeIndex]?.id;
  const filtering = value !== ALL_ROUTES;
  const label = routeOptionLabel(value, groupings);
  const noGroupings = !loading && groupings.length === 0;

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Route filter: ${label}`}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-medium max-w-[16rem] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50 ${
            filtering
              ? 'border-primary-500 bg-primary-500/10 text-primary-700'
              : 'border-gray-200 bg-surface text-gray-500 hover:text-slate-800 hover:bg-gray-50'
          }`}
        >
          <span className="text-[10px] uppercase tracking-wide text-gray-400">Route</span>
          <span className="truncate">{label}</span>
          <FaChevronDown className="text-[8px] flex-shrink-0" />
        </button>
        {filtering && (
          <button
            type="button"
            onClick={() => onChange(ALL_ROUTES)}
            title="Clear route filter"
            aria-label="Clear route filter"
            className="w-6 h-6 inline-flex items-center justify-center rounded text-gray-400 hover:text-slate-800 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50"
          >
            <FaTimes className="text-[10px]" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-40 w-[22rem] max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-surface-raised shadow-lg overflow-hidden">
          <div className="relative p-2 border-b border-gray-100">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls="period-summary-route-listbox"
              aria-autocomplete="list"
              aria-activedescendant={activeId ? `route-opt-${activeId}` : undefined}
              aria-label="Search routes"
              value={queryText}
              onChange={(e) => {
                setQueryText(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search routes…"
              autoComplete="off"
              className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-200 rounded-md bg-surface text-slate-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-500"
            />
          </div>

          <ul
            id="period-summary-route-listbox"
            role="listbox"
            aria-label="Routes"
            className="max-h-72 overflow-y-auto py-1"
          >
            {loading && <li className="px-3 py-3 text-[11px] text-gray-400">Loading routes…</li>}
            {noGroupings && (
              <li className="px-3 py-3 text-[11px] text-gray-500">
                No route groupings set up yet — add them under Settings → Route Summary.
              </li>
            )}
            {!loading && !noGroupings && filtered.length === 0 && (
              <li className="px-3 py-3 text-[11px] text-gray-400">No route matches that search</li>
            )}
            {filtered.map((item) => {
              const selected = item.id === value;
              const active = item.id === activeId;
              return (
                <li key={item.id} role="none">
                  <button
                    type="button"
                    id={`route-opt-${item.id}`}
                    role="option"
                    aria-selected={selected}
                    onClick={() => choose(item.id)}
                    onMouseEnter={() => setActiveIndex(filtered.findIndex((o) => o.id === item.id))}
                    title={item.description || item.label}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] ${
                      active ? 'bg-primary-500/10' : ''
                    } ${selected ? 'text-primary-700 font-semibold' : 'text-slate-800'}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{item.label}</span>
                      {item.description && (
                        <span className="block truncate text-[10px] text-gray-400 font-normal">
                          {item.description}
                        </span>
                      )}
                    </span>
                    {item.stat && (
                      <span className="text-[10px] text-gray-400 font-mono tabular-nums whitespace-nowrap">
                        {item.stat}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RoutePicker;
